<template>
  <div class="generate-bills-page">
    <!-- 顶部工具栏 -->
    <a-card class="toolbar-card" :bordered="false">
      <div class="toolbar-row">
        <div class="toolbar-left">
          <div class="page-title">批量生成账单</div>
          <div class="page-sub muted">按年份/月度为小区批量生成账单，并可查看历史记录。</div>
        </div>

        <div class="toolbar-right">
          <a-button icon="reload" :loading="loadingHistory" @click="loadHistory">
            刷新记录
          </a-button>
        </div>
      </div>
    </a-card>

    <!-- 表单卡片 -->
    <a-card class="content-card" :bordered="false">
      <template #title>
        <div class="section-title-row">
          <span>生成参数</span>
          <a-tag color="blue">小区 · 批量</a-tag>
        </div>
      </template>

      <a-alert
        v-if="message.text"
        :message="message.text"
        :type="message.type"
        show-icon
        style="margin-bottom: 12px"
      />

      <a-form layout="vertical" class="form-wrap">
        <a-row :gutter="16">
          <a-col :xs="24" :md="12">
            <a-form-item label="年份 *">
              <a-select v-model="form.year" placeholder="请选择年份" allowClear>
                <a-select-option v-for="y in years" :key="y" :value="y">{{ y }}年</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :xs="24" :md="12">
            <a-form-item label="月份 *">
              <a-select v-model="form.month" placeholder="请选择月份" allowClear>
                <a-select-option v-for="m in 12" :key="m" :value="m">{{ m }}月</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :xs="24" :md="12">
            <a-form-item label="费用类型">
              <a-select v-model="form.feeType">
                <a-select-option v-for="t in feeTypes" :key="t" :value="t">{{ t }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :xs="24" :md="12">
            <a-form-item label="每平米费用（元）">
              <a-input-number
                v-model="form.amountPerSqm"
                :min="0"
                :step="0.01"
                :precision="2"
                style="width: 100%"
                placeholder="请输入每平米费用"
              />
            </a-form-item>
          </a-col>

          <a-col :xs="24">
            <a-form-item label="生成说明（可选）">
              <a-textarea
                v-model="form.description"
                :rows="3"
                placeholder="请输入生成说明（例如：2025年12月物业费批量生成）"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <div class="form-actions">
          <a-button type="primary" :loading="loading" @click="handleGenerate">
            生成账单
          </a-button>
          <a-button :disabled="loading" @click="resetForm">
            清空
          </a-button>
        </div>
      </a-form>
    </a-card>

    <!-- 历史记录 -->
    <a-card class="content-card history-card" :bordered="false">
      <template #title>
        <div class="history-titlebar">
          <div class="section-title-row">
            <span>账单生成记录</span>
            <a-tag color="purple">{{ filteredHistory.length }} 条</a-tag>
          </div>

          <!-- ✅ 小筛选条 -->
          <div class="history-filter">
            <a-select
              v-model="historyFilters.year"
              allowClear
              placeholder="年份"
              style="width: 120px"
            >
              <a-select-option v-for="y in historyYearOptions" :key="y" :value="y">
                {{ y }}年
              </a-select-option>
            </a-select>

            <a-select
              v-model="historyFilters.month"
              allowClear
              placeholder="月份"
              style="width: 120px"
            >
              <a-select-option v-for="m in 12" :key="m" :value="m">
                {{ m }}月
              </a-select-option>
            </a-select>

            <a-select
              v-model="historyFilters.feeType"
              allowClear
              placeholder="费用类型"
              style="width: 160px"
            >
              <a-select-option v-for="t in historyFeeTypeOptions" :key="t" :value="t">
                {{ t }}
              </a-select-option>
            </a-select>

            <a-button
              style="margin-left: 8px"
              :disabled="!hasHistoryFilter"
              @click="resetHistoryFilters"
            >
              清空筛选
            </a-button>
          </div>
        </div>
      </template>

      <a-spin :spinning="loadingHistory">
        <div v-if="filteredHistory.length === 0" class="empty-box">
          <span v-if="history.length === 0">暂无生成记录</span>
          <span v-else>暂无符合筛选条件的记录</span>
        </div>

        <div v-else class="history-list">
          <div
            v-for="rec in filteredHistory"
            :key="`${rec.create_time}-${rec.fee_period}-${rec.fee_type}`"
            class="history-item"
          >
            <div class="history-top">
              <div class="history-title">
                <span class="strong">{{ rec.fee_period }}</span>
                <a-tag color="geekblue" style="margin-left: 8px">{{ rec.fee_type }}</a-tag>
              </div>

              <div class="history-count">
                <span class="muted">生成数量</span>
                <span class="count-num">{{ rec.generated_count }}</span>
              </div>
            </div>

            <div class="history-meta muted">
              生成时间：{{ formatDate(rec.create_time) }}
            </div>
          </div>
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script>
import request from "@/utils/request";

export default {
  name: "BatchGenerateBills",
  data() {
    return {
      years: [2024, 2025, 2026],
      feeTypes: ["物业费", "停车费", "水电费", "燃气费"],

      form: {
        year: undefined,
        month: undefined,
        feeType: "物业费",
        amountPerSqm: 3.5,
        description: "",
      },

      message: {
        text: "",
        type: "info", // info | success | warning | error
      },

      loading: false,
      loadingHistory: false,
      history: [],

      // ✅ 历史筛选
      historyFilters: {
        year: undefined,
        month: undefined,
        feeType: undefined,
      },
    };
  },
  computed: {
    hasHistoryFilter() {
      const f = this.historyFilters;
      return !!(f.year || f.month || f.feeType);
    },

    historyYearOptions() {
      const set = new Set();
      (this.history || []).forEach((r) => {
        const y = this.getYear(r);
        if (y) set.add(y);
      });
      return Array.from(set).sort((a, b) => b - a);
    },

    historyFeeTypeOptions() {
      const set = new Set();
      (this.history || []).forEach((r) => {
        const t = (r && r.fee_type) ? String(r.fee_type) : "";
        if (t) set.add(t);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },

    filteredHistory() {
      const f = this.historyFilters;

      return (this.history || [])
        .filter((r) => (f.year ? this.getYear(r) === Number(f.year) : true))
        .filter((r) => (f.month ? this.getMonth(r) === Number(f.month) : true))
        .filter((r) => (f.feeType ? String(r.fee_type || "") === String(f.feeType) : true))
        .slice()
        .sort((a, b) => {
          // create_time 是 "Sun, 21 Dec 2025 17:39:33 GMT"
          const ta = Date.parse(a.create_time || "") || 0;
          const tb = Date.parse(b.create_time || "") || 0;
          return tb - ta; // 新的在前
        });
    },
  },
  mounted() {
    this.loadHistory();
  },
  methods: {
    formatDate(ts) {
      const d = new Date(ts);
      const datePart = d.toLocaleDateString("zh-CN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      });
      const timePart = d.toLocaleTimeString("zh-CN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "UTC",
      });
      return `${datePart} ${timePart}`;
    },

    setMessage(text, type = "info") {
      this.message = { text, type };
      if (text) {
        setTimeout(() => (this.message.text = ""), 3000);
      }
    },

    resetForm() {
      this.form = {
        year: undefined,
        month: undefined,
        feeType: this.feeTypes[0],
        amountPerSqm: 3.5,
        description: "",
      };
      this.setMessage("", "info");
    },

    // ✅ 清空历史筛选
    resetHistoryFilters() {
      this.historyFilters = { year: undefined, month: undefined, feeType: undefined };
    },

    async loadHistory() {
      this.loadingHistory = true;
      try {
        // ✅ 改用本地 API
        const res = await request({
          url: "/electricity/property-bills/history/",
          method: "get",
        });
        const j = res && res.data ? res.data : res;
        if (j.success && Array.isArray(j.data)) {
          this.history = j.data;
          if (this.historyFilters.year && !this.historyYearOptions.includes(Number(this.historyFilters.year))) {
            this.historyFilters.year = undefined;
          }
          if (this.historyFilters.feeType && !this.historyFeeTypeOptions.includes(String(this.historyFilters.feeType))) {
            this.historyFilters.feeType = undefined;
          }
          if (this.historyFilters.month) {
            const m = Number(this.historyFilters.month);
            if (!(m >= 1 && m <= 12)) this.historyFilters.month = undefined;
          }
        } else {
          console.warn("加载历史失败", j.error);
        }
      } catch (e) {
        console.error("获取生成记录失败:", e);
      } finally {
        this.loadingHistory = false;
      }
    },

    async handleGenerate() {
      if (!this.form.year || !this.form.month) {
        this.setMessage("请选择年份和月份", "error");
        return;
      }

      this.loading = true;
      this.setMessage("", "info");

      try {
        // ✅ 改用本地 API
        const res = await request({
          url: "/electricity/property-bills/generate-for-period/",
          method: "post",
          data: {
            year: this.form.year,
            month: this.form.month,
            fee_type: this.form.feeType,
            amount_per_sqm: this.form.amountPerSqm,
            description: this.form.description,
          },
        });

        const j = res && res.data ? res.data : res;
        if (j.success) {
          // 使用后端返回的完整消息（包含跳过数量信息）
          const msg = j.message || `成功生成 ${j.generated_count} 个账单！`;
          this.setMessage(msg, "success");
          this.resetForm();
          await this.loadHistory();
        } else {
          this.setMessage(j.error || j.detail || "生成账单失败", "error");
        }
      } catch (e) {
        console.error("生成账单失败:", e);
        const errMsg = e.response?.data?.detail || e.response?.data?.error || "网络错误，请检查网络连接";
        this.setMessage(errMsg, "error");
      } finally {
        this.loading = false;
      }
    },
    parseFeePeriod(feePeriod) {
      // 支持 "2025年10月" / "2025 年 10 月" / 以及容错
      const s = String(feePeriod || "").replace(/\s+/g, "");
      const m = s.match(/(\d{4})年(\d{1,2})月/);
      if (!m) return { year: null, month: null };
      return { year: Number(m[1]), month: Number(m[2]) };
    },
    getYear(rec) {
      return this.parseFeePeriod(rec.fee_period).year;
    },
    getMonth(rec) {
      return this.parseFeePeriod(rec.fee_period).month;
    },

  },
};
</script>

<style scoped>
.generate-bills-page {
  padding: 16px;
}

.toolbar-card,
.content-card {
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.toolbar-card {
  margin-bottom: 16px;
}

.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.page-title {
  font-size: 18px;
  font-weight: 900;
  line-height: 1.2;
}

.page-sub {
  margin-top: 6px;
}

.toolbar-right {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-wrap {
  max-width: 900px;
}

.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.history-card {
  margin-top: 16px;
}

/* ✅ 标题 + 筛选条 同行布局 */
.history-titlebar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.history-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.empty-box {
  text-align: center;
  padding: 22px 12px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 14px;
  color: rgba(0, 0, 0, 0.45);
  background: rgba(0, 0, 0, 0.02);
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-item {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  padding: 14px 14px;
  background: #fff;
  transition: all 0.2s ease;
}

.history-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}

.history-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.history-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.strong {
  font-weight: 900;
}

.history-count {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.count-num {
  font-size: 18px;
  font-weight: 900;
  color: rgba(0, 0, 0, 0.85);
}

.history-meta {
  margin-top: 8px;
}

.muted {
  color: rgba(0, 0, 0, 0.45);
}
</style>
